import { useState, useEffect, useCallback } from 'react';
import apiClient from '../lib/apiClient';

/**
 * useTheme — single source of truth for theme state.
 *
 * Precedence:
 *   1. localStorage 'aif_theme' value (explicit user choice)
 *   2. Backend preference 'ui_theme' (cross-device sync on login)
 *   3. OS prefers-color-scheme (when no stored value)
 *   4. Light (the :root default in tokens.css)
 *
 * Supported themes: 'light' | 'dark' | 'outdoor' | null (= system default)
 */
export function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = localStorage.getItem('aif_theme');
    if (stored === 'dark' || stored === 'light' || stored === 'outdoor') return stored;
    // No stored preference: follow OS
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [isSystemMode, setIsSystemMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem('aif_theme');
  });

  // Apply theme class to html element whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark', 'light', 'outdoor');
    if (theme === 'dark') root.classList.add('dark');
    else if (theme === 'light') root.classList.add('light');
    else if (theme === 'outdoor') root.classList.add('outdoor');
    // When .light is present, @media (prefers-color-scheme: dark) is blocked via :root:not(.light)

    if (!isSystemMode) {
      localStorage.setItem('aif_theme', theme);
    }

    // Update meta theme-color
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      const colorMap = { dark: '#0f0f1a', light: '#ffffff', outdoor: '#ffffff' };
      meta.setAttribute('content', colorMap[theme] || '#ffffff');
    }
  }, [theme, isSystemMode]);

  // Clear isSystemMode after one render cycle so subsequent explicit theme choices persist
  useEffect(() => {
    if (isSystemMode) {
      const timer = setTimeout(() => setIsSystemMode(false), 0);
      return () => clearTimeout(timer);
    }
  }, [isSystemMode]);

  // Listen for OS theme changes when user has no explicit preference set
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const stored = localStorage.getItem('aif_theme');
      // Only follow OS if user hasn't explicitly chosen light or dark (outdoor is always explicit)
      if (!stored || stored === 'system') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);  // older browsers
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  // Backend sync: load preference on mount (cross-device persistence)
  useEffect(() => {
    const stored = localStorage.getItem('aif_theme');
    if (stored) return; // localStorage takes priority — user already chose on this device
    const token = localStorage.getItem('token');
    if (!token) return; // not logged in
    apiClient.get('/users/me/navigation-preferences')
      .then(res => {
        const prefs = (res.data ?? res)?.preferences || {};
        const backendTheme = prefs.ui_theme;
        if (backendTheme && ['light', 'dark', 'outdoor'].includes(backendTheme)) {
          setTheme(backendTheme);
          localStorage.setItem('aif_theme', backendTheme);
        }
      })
      .catch(() => {}); // silent — offline or not logged in
  }, []);

  // Legacy API for existing components that use { isDark, toggle }
  const isDark = theme === 'dark';

  // Explicit setter — clears system-follow flag so choice persists to localStorage + backend
  const setThemeExplicit = useCallback((newTheme) => {
    setIsSystemMode(false);
    setTheme(newTheme);
    // Fire-and-forget backend sync
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.patch('/users/me/navigation-preferences', {
        preferences: { ui_theme: newTheme },
      }).catch(() => {}); // silent — localStorage is the fast-path fallback
    }
  }, []);

  const toggle = useCallback(() => setThemeExplicit(theme === 'dark' ? 'light' : 'dark'), [theme, setThemeExplicit]);

  // Clear explicit preference and return to system default
  const resetToSystem = useCallback(() => {
    setIsSystemMode(true);
    localStorage.removeItem('aif_theme');
    const osTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    setTheme(osTheme);
    // Clear backend preference too
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.patch('/users/me/navigation-preferences', {
        preferences: { ui_theme: null },
      }).catch(() => {});
    }
  }, []);

  return { theme, setTheme: setThemeExplicit, isDark, toggle, resetToSystem };
}
